import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Rating,
  Card,
  CardContent,
  Container,
  Grid,
  Avatar,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { getApprovedReviews } from '../../apis/Admin';
import StarIcon from '@mui/icons-material/Star';
import Navbar from '../mainPage/components/Navbar';
import Footer from '../mainPage/components/Footer';

const ReviewDetailed = () => {
  const [searchParams] = useSearchParams();
  const reviewIdParam = searchParams.get('id');
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [otherReviews, setOtherReviews] = useState([]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const reviewsData = await getApprovedReviews();
        const reviewsList = Array.isArray(reviewsData)
          ? reviewsData
          : Array.isArray(reviewsData?.data)
            ? reviewsData.data
            : Array.isArray(reviewsData?.reviews)
              ? reviewsData.reviews
              : [];

        // Filter only approved reviews
        const approvedReviews = reviewsList.filter(
          (review) => review.approved || review.is_approved,
        );

        // Find selected review if ID is provided
        if (reviewIdParam) {
          const selected = approvedReviews.find(
            (review) =>
              String(review.id || review.review_id) === String(reviewIdParam),
          );
          if (selected) {
            setSelectedReview(selected);
            // Set other reviews (excluding selected)
            setOtherReviews(
              approvedReviews.filter(
                (review) =>
                  String(review.id || review.review_id) !==
                  String(reviewIdParam),
              ),
            );
          } else {
            // If review not found, show all reviews
            setOtherReviews(approvedReviews);
          }
        } else {
          // No ID provided, show all reviews
          setOtherReviews(approvedReviews);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching approved reviews:', error);
        setLoading(false);
      }
    };

    fetchReviews();
  }, [reviewIdParam]);

  if (loading) {
    return (
      <>
        <Navbar hideLoginButton={false} enableScrollEffect={true} />
        <Box
          sx={{
            width: '100%',
            minHeight: '80vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pt: 15,
          }}
        >
          <CircularProgress sx={{ color: '#5965A6' }} />
        </Box>
      </>
    );
  }

  return (
    <>
      <Navbar hideLoginButton={false} enableScrollEffect={true} />
      <Box
        sx={{
          width: '100%',
          minHeight: '100vh',
          bgcolor: '#f8fafc',
          pt: 12,
          pb: 8,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              color: '#1e293b',
              mt: 4,
              textAlign: 'center',
            }}
          >
            Reviews
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.875rem', md: '0.9375rem' },
              color: '#64748b',
              mb: 4,
              textAlign: 'center',
            }}
          >
            Read what our users have to say about our product and services.
          </Typography>
        </Box>
        <Container maxWidth="lg">
          {/* Selected Review Detail Section */}
          {selectedReview && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <Card
                sx={{
                  mb: 6,
                  borderRadius: '20px',
                  background:
                    'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid transparent',
                  backgroundImage:
                    'linear-gradient(white, white), linear-gradient(135deg, #0056D2 0%, #002C6C 100%)',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                  boxShadow:
                    '0 8px 32px rgba(0, 86, 210, 0.12), 0 2px 8px rgba(0, 44, 108, 0.08)',
                  overflow: 'hidden',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '6px',
                    background:
                      'linear-gradient(90deg, #0056D2 0%, #002C6C 100%)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 5 }, pt: { xs: 4, md: 6 } }}>
                  {/* Profile, Email, Rating Section - Horizontal Layout */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      alignItems: { xs: 'flex-start', md: 'center' },
                      gap: { xs: 3, md: 4 },
                      mb: 4,
                      pb: 4,
                      borderBottom: '2px solid',
                      borderImage:
                        'linear-gradient(90deg, #0056D2 0%, #002C6C 100%) 1',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '60px',
                        height: '2px',
                        background:
                          'linear-gradient(90deg, #0056D2 0%, #002C6C 100%)',
                      },
                    }}
                  >
                    {/* Avatar and Name */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2.5,
                        flex: { md: '0 0 auto' },
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '-6px',
                            left: '-6px',
                            right: '-6px',
                            bottom: '-6px',
                            borderRadius: '50%',
                            background:
                              'linear-gradient(135deg, rgba(91, 160, 242, 0.3) 0%, rgba(123, 179, 245, 0.3) 100%)',
                            zIndex: 0,
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: { xs: 64, md: 80 },
                            height: { xs: 64, md: 80 },
                            background:
                              'linear-gradient(135deg, #5BA0F2 0%, #7BB3F5 100%)',
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: { xs: '1.5rem', md: '2rem' },
                            boxShadow:
                              '0 6px 20px rgba(91, 160, 242, 0.25), 0 2px 8px rgba(123, 179, 245, 0.2)',
                            border: '3px solid #ffffff',
                            position: 'relative',
                            zIndex: 1,
                          }}
                        >
                          {(selectedReview.name || 'A').charAt(0).toUpperCase()}
                        </Avatar>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            color: '#1e293b',
                            fontSize: { xs: '1.125rem', md: '1.25rem' },
                            fontWeight: 700,
                            mb: 0.75,
                            letterSpacing: '-0.02em',
                          }}
                        >
                          {selectedReview.name || 'Anonymous'}
                        </Typography>
                        {selectedReview.organization && (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 2,
                              py: 0.5,
                              borderRadius: '20px',
                              background:
                                'linear-gradient(135deg, rgba(91, 160, 242, 0.1) 0%, rgba(123, 179, 245, 0.1) 100%)',
                              border: '1px solid rgba(91, 160, 242, 0.2)',
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#5BA0F2',
                                fontSize: { xs: '0.8125rem', md: '0.875rem' },
                                fontWeight: 600,
                              }}
                            >
                              {selectedReview.organization}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Email */}
                    {selectedReview.email && (
                      <Box
                        sx={{
                          flex: { md: '1 1 auto' },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            px: 2.5,
                            py: 1.25,
                            borderRadius: '12px',
                            background:
                              'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                            border: '1px solid rgba(91, 160, 242, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            boxShadow: '0 2px 8px rgba(91, 160, 242, 0.08)',
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#64748b',
                              fontSize: { xs: '0.8125rem', md: '0.875rem' },
                              fontWeight: 500,
                            }}
                          >
                            {selectedReview.email}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Rating */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: { xs: 'flex-start', md: 'flex-end' },
                        gap: 1.5,
                        px: { xs: 2, md: 3 },
                        py: { xs: 2, md: 2.5 },
                        borderRadius: '16px',
                        background:
                          'linear-gradient(135deg, rgba(91, 160, 242, 0.08) 0%, rgba(123, 179, 245, 0.08) 100%)',
                        border: '1px solid rgba(91, 160, 242, 0.15)',
                        boxShadow: '0 2px 8px rgba(91, 160, 242, 0.1)',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <Rating
                          value={selectedReview.rating || 0}
                          readOnly
                          precision={0.5}
                          size="large"
                          icon={
                            <StarIcon
                              sx={{
                                color: '#FFB020',
                                fontSize: { xs: '2rem', md: '2.25rem' },
                                filter:
                                  'drop-shadow(0 2px 4px rgba(255, 176, 32, 0.3))',
                              }}
                            />
                          }
                          emptyIcon={
                            <StarIcon
                              sx={{
                                color: '#e0e0e0',
                                fontSize: { xs: '2rem', md: '2.25rem' },
                              }}
                            />
                          }
                        />
                      </Box>
                      <Typography
                        sx={{
                          color: '#5BA0F2',
                          fontSize: { xs: '0.8125rem', md: '0.875rem' },
                          fontWeight: 700,
                        }}
                      >
                        {selectedReview.rating || 0} out of 5
                      </Typography>
                    </Box>
                  </Box>

                  {/* Review Text Section */}
                  <Box
                    sx={{
                      position: 'relative',
                      mt: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: '50px', md: '60px' },
                          height: { xs: '50px', md: '60px' },
                          borderRadius: '50%',
                          background:
                            'linear-gradient(135deg, rgba(91, 160, 242, 0.12) 0%, rgba(123, 179, 245, 0.12) 100%)',
                          border: '2px solid rgba(91, 160, 242, 0.25)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#5BA0F2',
                          fontSize: { xs: '1.5rem', md: '2rem' },
                          fontWeight: 700,
                          fontFamily: 'serif',
                          boxShadow: '0 4px 12px rgba(91, 160, 242, 0.15)',
                        }}
                      >
                        &ldquo;
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#1e293b',
                          fontSize: { xs: '1.125rem', md: '1.375rem' },
                          fontWeight: 700,
                        }}
                      >
                        Review by {selectedReview.name || 'Anonymous'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        position: 'relative',
                        pl: { xs: 2, md: 3 },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          background:
                            'linear-gradient(180deg, #5BA0F2 0%, #7BB3F5 100%)',
                          borderRadius: '2px',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#374151',
                          fontSize: { xs: '0.9375rem', md: '1rem' },
                          lineHeight: 1.8,
                          fontWeight: 400,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          letterSpacing: '0.01em',
                        }}
                      >
                        {selectedReview.review_text ||
                          selectedReview.review ||
                          'No review text provided.'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Other Reviews Section */}
          {otherReviews.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  color: '#1e293b',
                  mb: 4,
                  textAlign: 'center',
                }}
              >
                Check other reviews
              </Typography>

              <Grid container spacing={3}>
                {otherReviews.map((review, index) => {
                  const rating = review.rating || 0;
                  const reviewText = review.review_text || review.review || '';
                  const reviewerName = review.name || 'Anonymous';
                  const jobTitle = review.organization || '';
                  const reviewId = review.id || review.review_id || index;
                  const MAX_LENGTH = 150;
                  const displayText =
                    reviewText.length > MAX_LENGTH
                      ? reviewText.substring(0, MAX_LENGTH) + '...'
                      : reviewText;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={reviewId}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <Card
                          onClick={() => {
                            window.location.href = `/reviews?id=${reviewId}`;
                          }}
                          sx={{
                            height: '100%',
                            minHeight: '350px',
                            maxHeight: '350px',
                            borderRadius: '12px',
                            background: '#ffffff',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            '&:hover': {
                              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                              transform: 'translateY(-4px)',
                              border: '1px solid #0056D2',
                            },
                          }}
                        >
                          <CardContent
                            sx={{
                              p: 3,
                              display: 'flex',
                              flexDirection: 'column',
                              height: '100%',
                              flex: 1,
                              overflow: 'hidden',
                            }}
                          >
                            {/* Reviewer Info with Avatar */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2.5,
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 44,
                                  height: 44,
                                  background:
                                    'linear-gradient(135deg, #0056D2 0%, #002C6C 100%)',
                                  color: '#ffffff',
                                  fontWeight: 600,
                                  fontSize: '1rem',
                                  mr: 1.5,
                                }}
                              >
                                {reviewerName.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    color: '#1e293b',
                                    fontSize: '0.9375rem',
                                    fontWeight: 600,
                                    mb: 0.25,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {reviewerName}
                                </Typography>
                                {jobTitle && (
                                  <Typography
                                    sx={{
                                      color: '#6b7280',
                                      fontSize: '0.8125rem',
                                      fontWeight: 400,
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {jobTitle}
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            {/* Rating */}
                            <Box sx={{ mb: 2 }}>
                              <Rating
                                value={rating}
                                readOnly
                                precision={0.5}
                                icon={
                                  <StarIcon
                                    sx={{
                                      color: '#FFB020',
                                      fontSize: '1.125rem',
                                    }}
                                  />
                                }
                                emptyIcon={
                                  <StarIcon
                                    sx={{
                                      color: '#e0e0e0',
                                      fontSize: '1.125rem',
                                    }}
                                  />
                                }
                              />
                            </Box>

                            <Divider sx={{ color: 'grey' }} />

                            {/* Review Text */}
                            <Typography
                              sx={{
                                color: '#374151',
                                fontSize: '0.875rem',
                                lineHeight: 1.6,
                                my: 2,
                                flexGrow: 1,
                                fontWeight: 400,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {displayText}
                            </Typography>

                            {/* Read Full Review Button */}
                            {reviewText.length > MAX_LENGTH && (
                              <Box
                                sx={{
                                  mt: 'auto',
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                }}
                              >
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.href = `/reviews?id=${reviewId}`;
                                  }}
                                  sx={{
                                    color: '#FFB020',
                                    textTransform: 'none',
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                    p: 0,
                                    minWidth: 'auto',
                                    '&:hover': {
                                      backgroundColor: 'transparent',
                                      textDecoration: 'underline',
                                    },
                                  }}
                                >
                                  Read full review
                                </Button>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  );
                })}
              </Grid>
            </motion.div>
          )}

          {/* No reviews message */}
          {!selectedReview && otherReviews.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
              }}
            >
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '1.125rem',
                }}
              >
                No reviews available at the moment.
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default ReviewDetailed;
